import { useState } from 'react';
import { Button, IconButton, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { clsx } from 'clsx';

import type { ParsedToken } from '../../../types/QueryBuilder';
import { useQueryBuilderContext } from '../context';
import { useFilterBarContext } from '../FilterBarContext';
import { useQueryBuilderGridItem } from '../hooks/useQueryBuilderGridItem';
import { replaceToken } from '../utils';

import styles from './LogicBoolToken.module.css';

type LogicBoolParsedToken = Extract<ParsedToken, { type: 'LOGIC_BOOL' }>;

interface LogicBoolTokenProps {
  tokenKey: string;
  token: LogicBoolParsedToken;
  onChange: (value: 'AND' | 'OR') => void;
  onRemove: () => void;
  removable?: boolean;
}

export function LogicBoolToken({ tokenKey, token, onChange, onRemove, removable = true }: LogicBoolTokenProps) {
  const { dispatch, query } = useQueryBuilderContext();
  const { readonly, gridState } = useFilterBarContext();
  const { containerProps, cellProps } = useQueryBuilderGridItem(tokenKey, gridState);
  const [activePart, setActivePart] = useState<'operator' | 'remove' | null>(null);

  function handlePress() {
    const newValue = token.value === 'AND' ? 'OR' : 'AND';
    dispatch({ type: 'SET_QUERY', query: replaceToken(query, token, newValue) });
    onChange(newValue);
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- wrapper exists only to stop click propagation; interactive controls live inside
    <div className={styles.container} onClick={(e) => e.stopPropagation()}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- role="row" is provided via containerProps spread */}
      <div {...containerProps} className={styles.logicBoolToken}>
        {/* Operator cell */}
        <div {...cellProps}>
          <span className={styles.editableCell}>
            <span aria-hidden="true" className={styles.editableCellOverlay} />
            {readonly ? (
              <span data-part="operator" className={styles.label} aria-label={`Logic operator ${token.value}`}>
                {token.value}
              </span>
            ) : (
              <TooltipTrigger>
                <Button
                  data-part="operator"
                  className={clsx(styles.cell, {
                    [styles.cellFocused]: activePart === 'operator',
                    [styles.cellFocusedPrev]: activePart === 'remove',
                  })}
                  onFocus={() => setActivePart('operator')}
                  onBlur={() => setActivePart(null)}
                  onPress={handlePress}
                  aria-label={`Logic operator ${token.value}, press to toggle`}
                >
                  {token.value}
                </Button>
                <Tooltip>{`Toggle to ${token.value === 'AND' ? 'OR' : 'AND'}`}</Tooltip>
              </TooltipTrigger>
            )}
          </span>
        </div>

        {/* Remove cell */}
        {removable && (
          <div {...cellProps}>
            <TooltipTrigger>
              <IconButton
                className={clsx(styles.removeButton, {
                  [styles.cellFocusedNext]: activePart === 'operator',
                })}
                onFocus={() => setActivePart('remove')}
                onBlur={() => setActivePart(null)}
                icon="cancel"
                variant="minimal"
                size="small"
                aria-label="Remove operator"
                onPress={onRemove}
              />
              <Tooltip>Remove logic operator</Tooltip>
            </TooltipTrigger>
          </div>
        )}
      </div>
    </div>
  );
}

LogicBoolToken.displayName = 'LogicBoolToken';
